import {artemisService} from '../services/artemis.service';
import {AnalyticsUtil, ANALYTICS_EVENTS} from '../utils/analytics.util';
import {router} from '../utils/router.util';
import {campaignCache, getCampaignImage, hasOnlyExternalRedirectMedias} from '../utils/campaign.util';
import {ToolbarComponent} from '../components/toolbar.component';
import {ButtonComponent} from '../components/button.component';
import {authService} from '../services/auth.service';
import {StorageUtil} from '../utils/storage.util';
import {formatPhone} from '../utils/phone.util';
import {AACampaignResponseStatus, AACampaignResult, AAMedia, AAMediaTypes} from '@akross/artemis-ads-sdk';
import {AACampaign, AAAdWatchResult, AAMediaPlayResult} from '@akross/artemis-ads-sdk';
import {ArtemisRewardPage} from './artemis-reward.page';
import {ARTEMIS_ZONES, ARTEMIS_CONFIG} from '../constants/artemis.constants';
import {STORAGE_KEYS} from '../constants/storage.constants';
import {ROUTES} from '../constants/routes.constants';
import {checkCarrotAndExecute} from '../utils/carrot.util';
import {isSafariBrowser, openCarrotRedirectInNewTab, openUrlInNewTab} from '../utils/browser.util';
import {logger} from '../utils/logger.util';
import {MessageDialogComponent} from '../components/message-dialog.component';

type HomeState = 'loading' | 'error' | 'empty' | 'content';

export class HomePage {
  private container: HTMLElement;
  private state: HomeState = 'loading';
  private campaigns: AACampaign[] = [];
  private error: Error | null = null;
  private isRendering: boolean = false;
  private msisdn: string | null = null;
  private pendingCampaignId: string | null = null;
  private refreshTimer: number | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    // Evitar renderizações duplicadas
    if (this.isRendering) {
      logger.debug('HomePage: render() already in progress, skipping...');
      return;
    }

    logger.debug('HomePage: render() called');

    // Limpar timer anterior antes de iniciar novo render
    this.clearAutoRefresh();

    this.isRendering = true;
    this.state = 'loading';
    this.error = null;

    // Ler query params do sessionStorage (salvos pelo router antes de processar)
    // Sempre ler do sessionStorage (não usar cache) para garantir que só execute uma vez
    const cidFromStorage = sessionStorage.getItem(STORAGE_KEYS.QUERY_PARAM_CID);
    if (cidFromStorage) {
      this.pendingCampaignId = cidFromStorage;
      logger.debug('[HomePage] Query param cid capturado do sessionStorage');
      // Limpar IMEDIATAMENTE após ler para não reutilizar em renderizações futuras
      sessionStorage.removeItem(STORAGE_KEYS.QUERY_PARAM_CID);
    } else {
      // Limpar o pendingCampaignId se não há novo valor (evita reutilização)
      this.pendingCampaignId = null;
    }

    // Buscar MSISDN do usuário logado
    const userData = StorageUtil.getUserData();
    this.msisdn = userData?.msisdn || null;

    this.renderContent();

    try {
      // Carregar campanhas da zona de campanhas
      logger.debug('[HomePage] Carregando campanhas da zona de campanhas...');
      const campaignsResult : AACampaignResult | null = await artemisService.getCampaignsByZone(ARTEMIS_ZONES.APPS, ARTEMIS_CONFIG.DEFAULT_LIMIT).catch(error => {
        logger.error('Erro ao carregar campanhas:', error);
        return null;
      });

      // Tratar erro na requisição (campaignsResult é null)
      if (campaignsResult === null) {
        logger.error('[HomePage] Erro na requisição - campaignsResult é null');
        this.error = new Error('Erro ao carregar campanhas. Tente novamente mais tarde.');
        this.state = 'error';
        this.renderContent();
        return;
      }

      // Tratar os 3 status possíveis da SDK
      switch (campaignsResult.responseStatus) {
        case AACampaignResponseStatus.ERROR:
          logger.error('[HomePage] ResponseStatus ERROR recebido do Artemis.');
          this.error = new Error('Erro ao carregar campanhas. Tente novamente mais tarde.');
          this.state = 'error';
          this.renderContent();
          return;

        case AACampaignResponseStatus.EMPTY:
          logger.debug('[HomePage] ResponseStatus EMPTY recebido do Artemis.');
          this.state = 'empty';
          this.renderContent();
          return;

        case AACampaignResponseStatus.SUCCESS:
        default:
          // Continuar com o processamento normal
          break;
      }

      // Processar campanhas (status SUCCESS)
      const campaigns = campaignsResult.getCampaigns() || [];
      this.campaigns = campaigns;
      logger.debug('[HomePage] Campanhas carregadas:', campaigns.length);

      // Salvar campanhas no cache
      campaignCache.setCampaigns(campaigns);

      // Definir estado e exibir página imediatamente
      if (campaigns.length > 0) {
        this.state = 'content';
        // Enviar impressões em background (não bloqueia a exibição da página)
        const campaignsTrackingId = (campaigns[0] as any).trackingId || '';
        artemisService.sendCampaignsImpression(campaignsTrackingId, campaigns)
          .then(() => logger.debug('[HomePage] Evento de impression enviado para campanhas'))
          .catch(err => logger.error('[HomePage] Erro ao enviar impression:', err));
      } else {
        // Caso raro: SUCCESS mas sem campanhas
        this.state = 'empty';
      }

      this.renderContent();

      // Configurar timer de refresh automático baseado no campo "ka"
      this.setupAutoRefresh(campaignsResult);

      // Verificar se há um query param cid e clicar automaticamente na campanha
      this.handleAutoClickCampaign();
    } catch (error: unknown) {
      logger.error('Error rendering home page:', error);
      this.error = error instanceof Error ? error : new Error('Erro desconhecido');
      this.state = 'error';
      this.renderContent();
    } finally {
      this.isRendering = false;
    }
  }

  private renderContent(): void {
    this.container.innerHTML = this.buildHTML();
    this.attachEvents();
  }

  private buildHTML(): string {
    switch (this.state) {
      case 'loading':
        return this.buildLoadingHTML();
      case 'error':
        return this.buildErrorHTML();
      case 'empty':
        return this.buildEmptyHTML();
      case 'content':
        return this.buildContentHTML();
    }
  }

  private buildLoadingHTML(): string {
    return `
      <div class="campaigns-page">
        ${ToolbarComponent.render({
          showBackButton: false,
          showMenuButton: false,
          elevation: 2,
          logoImage: '/vivo-ads-logo.svg',
          logoAlt: 'Vivo Ads',
          logoAlign: 'left',
          hideSpacers: true
        })}
        <div class="campaigns-state">
          <div class="loading">
            <div class="spinner"></div>
            <p class="loading-text">Carregando campanhas. Por favor, aguarde</p>
          </div>
        </div>
      </div>
    `;
  }

  private buildErrorHTML(): string {
    return `
      <div class="campaigns-page">
        ${ToolbarComponent.render({
          showBackButton: false,
          showMenuButton: false,
          elevation: 2,
          logoImage: '/vivo-ads-logo.svg',
          logoAlt: 'Vivo Ads',
          logoAlign: 'left',
          hideSpacers: true
        })}
        <div class="campaigns-state">
          <div class="error-screen">
            <h2>Ops! Algo deu errado</h2>
            <p>${this.error?.message || 'Não foi possível carregar as campanhas.'}</p>
            <div id="retry-button-container"></div>
          </div>
        </div>
      </div>
    `;
  }

  private buildEmptyHTML(): string {
    return `
      <div class="campaigns-page">
        ${ToolbarComponent.render({
          showBackButton: false,
          showMenuButton: false,
          elevation: 2,
          logoImage: '/vivo-ads-logo.svg',
          logoAlt: 'Vivo Ads',
          logoAlign: 'left',
          hideSpacers: true
        })}
        <div class="campaigns-state">
          <div class="empty-screen">
            <p>Nenhuma campanha disponível no momento</p>
          </div>
        </div>
      </div>
    `;
  }

  private buildContentHTML(): string {
    return `
      <div class="campaigns-page">
        ${ToolbarComponent.render({
          showBackButton: false,
          showMenuButton: false,
          elevation: 2,
          logoImage: '/vivo-ads-logo.svg',
          logoAlt: 'Vivo Ads',
          logoAlign: 'left',
          hideSpacers: true
        })}
        <div class="campaigns-scroll">
          ${this.buildUserInfoHTML()}
          ${this.buildCampaignsListHTML()}
          ${this.buildFooterHTML()}
        </div>
      </div>
    `;
  }

  private buildUserInfoHTML(): string {
    if (!this.msisdn) {
      return '';
    }

    const formattedPhone = formatPhone(this.msisdn);

    return `
      <div class="campaigns-user-info">
        <span class="campaigns-user-info-label">Conectado como:</span>
        <span class="campaigns-user-info-phone">${this.escapeHtml(formattedPhone)}</span>
      </div>
    `;
  }

  private buildCampaignsListHTML(): string {
    if (this.campaigns.length === 0) {
      return '';
    }

    return `
      <div class="campaigns-list">
        ${this.campaigns.map(campaign => this.buildCampaignCardHTML(campaign)).join('')}
      </div>
    `;
  }

  private buildCampaignCardHTML(campaign: AACampaign): string {
    const escapedName = this.escapeHtml(campaign.mainData.layout?.title || campaign.name || '');
    const imageUrl = getCampaignImage(campaign);
    const sponsorIcon = campaign.mainData?.layout?.icon || '';
    const secondaryInfo = campaign.mainData?.layout?.secondaryInformation || '';

    return `
      <div class="campaign-card" data-campaign-id="${campaign.campaignUuid}">
        <div class="campaign-card-image-container">
          ${imageUrl
            ? `<img 
                 src="${imageUrl}" 
                 alt="${escapedName}" 
                 class="campaign-card-image"
                 onerror="this.onerror=null; this.style.display='none';"
               />`
            : ''
          }
        </div>
        <div class="campaign-card-content">
          <h3 class="campaign-card-mission-title">${escapedName}</h3>
          ${secondaryInfo
            ? `<div class="campaign-card-description-wrapper">
                 ${sponsorIcon
                   ? `<div class="campaign-card-sponsor-badge">
                        <img 
                          src="${sponsorIcon}" 
                          alt="Sponsor" 
                          class="campaign-card-sponsor-icon"
                          onerror="this.onerror=null; this.style.display='none';"
                        />
                      </div>`
                   : ''
                 }
                 <p class="campaign-card-description">${this.escapeHtml(secondaryInfo)}</p>
               </div>`
            : ''
          }
        </div>
      </div>
    `;
  }

  private buildFooterHTML(): string {
    return `
      <footer class="campaigns-footer" id="campaigns-footer">
        <div class="campaigns-footer-links">
          <a href="#${ROUTES.HOW_IT_WORKS}" class="campaigns-footer-link" id="how-it-works-link">Como funciona</a>
          <a href="https://vivo.com.br/a-vivo/informacoes-aos-clientes/centro-de-privacidade/privacidade-e-seguranca/politicas-de-privacidade" target="_blank" class="campaigns-footer-link">Política de privacidade</a>
          <a href="https://internetgratis.vivo.com.br/wifi#footer-link3" target="_blank" class="campaigns-footer-link">Regulamento</a>
          <button class="campaigns-footer-link campaigns-footer-link-button" id="logout-button">Sair</button>
        </div>
      </footer>
    `;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private attachEvents(): void {
    // Toolbar events (sempre anexar, mesmo em estados de loading/erro/vazio)
    ToolbarComponent.attachEvents(this.container, {
      showBackButton: false,
      showMenuButton: false
    });

    // Botão de retry (usando ButtonComponent para manter consistência com login)
    const retryButtonContainer = document.getElementById('retry-button-container');
    if (retryButtonContainer) {
      const retryButton = ButtonComponent.createPrimaryButton({
        text: 'Tentar novamente',
        id: 'retry-button',
        fullWidth: true,
        onClick: () => {
          this.render();
        }
      });
      retryButtonContainer.appendChild(retryButton);
    }

    // Cliques nos cards de campanha
    const campaignCards = this.container.querySelectorAll('.campaign-card');
    campaignCards.forEach(card => {
      card.addEventListener('click', (e) => {
        const campaignId = (e.currentTarget as HTMLElement).getAttribute('data-campaign-id');
        if (campaignId) {
          this.onCampaignClick(campaignId);
        }
      });
    });

    // Footer events (apenas no estado content)
    if (this.state === 'content') {
      this.attachFooterEvents();
    }

    // Link "Como funciona"
    const howItWorksLink = this.container.querySelector('#how-it-works-link');
    if (howItWorksLink) {
      howItWorksLink.addEventListener('click', (e) => {
        e.preventDefault();
        router.navigate(ROUTES.HOW_IT_WORKS);
      });
    }
  }

  private attachFooterEvents(): void {
    const logoutButton = this.container.querySelector('#logout-button') as HTMLButtonElement;

    // Event listener para logout
    if (logoutButton) {
      logoutButton.addEventListener('click', async () => {
        await authService.logout();
        router.navigate(ROUTES.SPLASH);
      });
    }
  }

  /**
   * Executa uma campanha usando a mesma lógica do clique (considera tipo de mídias)
   */
  private async executeCampaign(campaign: AACampaign): Promise<void> {
    // Enviar evento de analytics
    AnalyticsUtil.track(ANALYTICS_EVENTS.CAMPAIGN_CLICKED, {
      campaignId: campaign.campaignUuid
    });

    // Enviar evento de click via Artemis (clique no card da campanha)
    const campaignTrackingId = (campaign as any).trackingId || '';
    if (campaignTrackingId) {
      artemisService.sendEvent('click', campaign.campaignUuid, campaignTrackingId)
        .catch(err => logger.error('[HomePage] Erro ao enviar click no Artemis:', err));
    }

    // Verificar se a campanha não é external_redirect e tem apenas 1 mídia
    const isNotExternalRedirect = !hasOnlyExternalRedirectMedias(campaign);
    const medias = campaign.mainData?.media || [];
    const hasSingleMedia = medias.length === 1;

    if (isNotExternalRedirect && hasSingleMedia) {
      // Executar o click do Artemis diretamente
      const media = medias[0] as AAMedia;
      await this.executeMediaClick(media, campaign);
    } else {
      // Navegar para página de detalhes normalmente
      router.navigate(`${ROUTES.CAMPAIGN}/${campaign.campaignUuid}`);
    }
  }

  private async onCampaignClick(campaignId: string): Promise<void> {
    // Buscar campanha do cache
    const campaign = campaignCache.getCampaign(campaignId);

    if (!campaign) {
      // Se não encontrou no cache, navegar para página de detalhes normalmente
      router.navigate(`${ROUTES.CAMPAIGN}/${campaignId}`);
      return;
    }

    await this.executeCampaign(campaign);
  }

  /**
   * Executa o click da mídia do Artemis diretamente na home
   */
  private async executeMediaClick(media: AAMedia, campaign: AACampaign): Promise<void> {
    logger.debug('[HomePage] executeMediaClick - iniciado, mediaId:', media.uuid);

    if (media.viewed) {
      logger.warnWithContext('HomePage', 'executeMediaClick - mídia já foi visualizada');
      return;
    }

    try {
      AnalyticsUtil.track(ANALYTICS_EVENTS.CHALLENGE_MEDIA_CLICKED, {
        campaignId: campaign.campaignUuid,
        mediaId: media.uuid
      });

      // Safari + banner: abrir no mesmo gesto (evita bloqueio de popup); não chamar media.click()
      if (media.type === AAMediaTypes.BANNER && isSafariBrowser()) {
        const clickUrl = media.externalUrl?.url;
        if (!clickUrl) {
          logger.warnWithContext('HomePage', 'executeMediaClick - mídia image sem externalUrl');
          return;
        }
        openCarrotRedirectInNewTab(clickUrl);
        artemisService.sendEvent('click', campaign.campaignUuid, media.requestId!, media.uuid);
        artemisService.sendEvent('clickthrough', campaign.campaignUuid, media.requestId!, media.uuid);
        return;
      }

      switch (media.type) {
        case AAMediaTypes.BANNER: {
          const mediaResult = await media.click();
          await this.handleMediaResult(mediaResult, media);
          break;
        }
        case AAMediaTypes.PROGRAMMATIC_V2:
          await checkCarrotAndExecute(async () => {
            const mediaResult = await media.click();
            await this.handleMediaResult(mediaResult, media);
          });
          break;
        default: {
          const mediaResult = await media.click();
          await this.handleMediaResult(mediaResult, media);
          break;
        }
      }
    } catch (error: unknown) {
      logger.error('[HomePage] executeMediaClick - erro geral:', error);
    }
  }


  /**
   * Lê um query parameter da URL
   * Primeiro tenta usar o valor armazenado (capturado no início do render)
   * Depois tenta ler da URL atual
   */
  private getQueryParam(name: string): string | null {
    // Se temos um valor armazenado (capturado antes do router processar), usar ele
    if (name === 'cid' && this.pendingCampaignId) {
      return this.pendingCampaignId;
    }

    // Tentar no search tradicional
    const searchParams = new URLSearchParams(window.location.search);
    const searchValue = searchParams.get(name);
    if (searchValue) {
      return searchValue;
    }

    // Tentar no hash atual
    const hash = window.location.hash.slice(1);
    const hashQueryIndex = hash.indexOf('?');
    if (hashQueryIndex >= 0) {
      const hashQuery = hash.substring(hashQueryIndex + 1);
      const hashParams = new URLSearchParams(hashQuery);
      return hashParams.get(name);
    }

    return null;
  }

  /**
   * Remove o parâmetro cid da URL (tanto do query string quanto do hash)
   * Isso evita que o parâmetro seja processado novamente ao navegar de volta para a home
   */
  private removeCidFromUrl(): void {
    try {
      const url = new URL(window.location.href);
      const hash = window.location.hash.slice(1);

      // Remover cid do query string da URL base
      if (url.searchParams.has('cid')) {
        url.searchParams.delete('cid');
        logger.debug('[HomePage] Removendo cid do query string da URL base');
      }

      // Remover cid do hash se existir
      let newHash = hash;
      const hashQueryIndex = hash.indexOf('?');
      if (hashQueryIndex >= 0) {
        const route = hash.substring(0, hashQueryIndex);
        const hashQuery = hash.substring(hashQueryIndex + 1);
        const hashParams = new URLSearchParams(hashQuery);

        if (hashParams.has('cid')) {
          hashParams.delete('cid');
          const newHashQuery = hashParams.toString();
          newHash = newHashQuery ? `${route}?${newHashQuery}` : route;
          logger.debug('[HomePage] Removendo cid do hash da URL');
        }
      }

      // Atualizar a URL sem recarregar a página
      const newUrl = url.pathname + (url.search ? url.search : '') + (newHash ? `#${newHash}` : '');
      window.history.replaceState(null, '', newUrl);

      logger.debug('[HomePage] URL atualizada sem o parâmetro cid');
    } catch (error) {
      logger.error('[HomePage] Erro ao remover cid da URL:', error);
    }
  }

  /**
   * Verifica se há um query param cid e executa automaticamente a campanha correspondente
   */
  private async handleAutoClickCampaign(): Promise<void> {
    const campaignId = this.getQueryParam('cid');

    if (!campaignId) {
      return;
    }

    logger.debug('[HomePage] Query param cid encontrado:', campaignId);

    // Limpar o pendingCampaignId após usar para evitar reexecução
    this.pendingCampaignId = null;

    // Aguardar um pouco para garantir que as campanhas foram carregadas
    await new Promise(resolve => setTimeout(resolve, 100));

    // Buscar campanha nas campanhas carregadas
    const campaign = this.campaigns.find(
      c => c.campaignUuid === campaignId || c.id === campaignId
    );

    if (campaign) {
      logger.debug('[HomePage] Campanha encontrada, executando automaticamente...');

      // Remover o parâmetro cid da URL antes de executar a campanha
      // Isso evita que o parâmetro seja processado novamente ao voltar para a home
      this.removeCidFromUrl();

      await this.executeCampaign(campaign);
      return;
    }

    logger.warnWithContext('HomePage', 'Campanha não encontrada para o cid:', campaignId);

    // Remover o parâmetro mesmo se a campanha não foi encontrada
    this.removeCidFromUrl();
  }

  /**
   * Processa o resultado da mídia - verifica se foi REWARDED, BANNER_CLICK e navega para página de sucesso
   */
  private async handleMediaResult(result: AAAdWatchResult, media: AAMedia): Promise<void> {
    logger.debug('[HomePage] handleMediaResult - processando resultado');

    const resultValue = result?.result || result?.playResult || result?.status;

    switch (resultValue) {
      case AAMediaPlayResult.NO_FILL: {
        logger.debug('[HomePage] handleMediaResult - NO_FILL, exibindo dialog');
        AnalyticsUtil.track(ANALYTICS_EVENTS.MEDIA_NO_FILL, {
          campaignId: (media as any).campaignId,
          mediaId: media.uuid
        });
        MessageDialogComponent.show({
          message: 'Nenhuma publicidade foi encontrada para você neste momento. Tente novamente mais tarde.'
        });
        return;
      }
      case AAMediaPlayResult.BANNER_CLICK: {
        const clickUrl = result?.media?.externalUrl?.url ?? media.externalUrl?.url;
        if (!clickUrl) return;

        if (isSafariBrowser()) {
          openCarrotRedirectInNewTab(clickUrl);
          return;
        }
        await checkCarrotAndExecute(async () => {
          openUrlInNewTab(clickUrl);
        });
        return;
      }
      case AAMediaPlayResult.REWARDED: {
        logger.debug('[HomePage] Mídia recompensada! Navegando para página de sucesso...');
        ArtemisRewardPage.openPage(media);
        return;
      }
      case AAMediaPlayResult.ERROR:
      case AAMediaPlayResult.CONNECTION_ERROR: {
        logger.debug('[HomePage] handleMediaResult - erro, exibindo dialog');
        AnalyticsUtil.trackMediaError(ANALYTICS_EVENTS.MEDIA_ERROR, {
          campaignId: (media as any).campaignId,
          mediaId: media.uuid,
          errorMessage: result.error?.cause
        });
        MessageDialogComponent.show({
          message: 'Algo deu errado ao exibir a publicidade. Tente novamente mais tarde.'
        });
        return;
      }
      default: {
        // SKIPPED, CANCELED e outros: sem ação por enquanto
      }
    }
  }

  /**
   * Configura o timer de refresh automático baseado no campo "ka" da resposta
   */
  private setupAutoRefresh(campaignsResult: AACampaignResult | null): void {
    // Limpar timer anterior se existir
    this.clearAutoRefresh();

    // Extrair valor de "ka" da resposta
    // O campo "ka" pode estar diretamente no objeto ou em uma propriedade específica
    const campaignsKa = campaignsResult?.campaignResponse?.ka;

    logger.debug('[HomePage] Verificando valor de ka para timer de refresh');

    // Se não houver valor válido, não configurar timer
    if (typeof campaignsKa !== 'number' || campaignsKa <= 0) {
      logger.debug('[HomePage] Nenhum valor de ka válido encontrado, timer de refresh não será configurado');
      return;
    }

    // Converter para milissegundos (ka vem em segundos do adserver)
    const kaInMs = campaignsKa * 1000;
    logger.debug('[HomePage] Timer de refresh configurado com ka:', campaignsKa, 'segundos');

    // Configurar timer para fazer refresh após o tempo especificado
    this.refreshTimer = window.setTimeout(() => {
      // Verificar se ainda estamos na rota /home antes de executar o refresh
      const currentRoute = router.getCurrentRoute();
      if (currentRoute !== ROUTES.HOME) {
        logger.debug('[HomePage] Timer de refresh ignorado - não está mais na rota /home. Rota atual:', currentRoute);
        this.clearAutoRefresh();
        return;
      }

      logger.debug('[HomePage] Timer de refresh executado, recarregando página...');
      this.render();
    }, kaInMs);
  }

  /**
   * Limpa o timer de refresh automático
   */
  private clearAutoRefresh(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
      logger.debug('[HomePage] Timer de refresh limpo');
    }
  }

  /**
   * Limpa recursos quando a página é destruída ou não está mais ativa
   */
  destroy(): void {
    this.clearAutoRefresh();
  }

}

